import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import {
    ApplicationFacade,
    BookRidePlan,
    BookRidePriorityType,
    GeocodeAddress,
    GeocodeAddressType,
    RidePlannerFacade
} from '@ridango/components';
import { AppStartupService, DateBuilder } from '@ridango/core';

import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';

import Env from '@ridango/core/env';
@Component({
    selector: 'rwidget-plan-ride',
    templateUrl: './plan-ride.component.html',
    styleUrls: ['./plan-ride.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanRideComponent implements OnInit {
    readonly MAX_BOOK_DATE = 5;

    @Input() set selectedPickup(pickup: GeocodeAddress) {
        if (pickup) {
            this.planRideForm.patchValue({ pickupFeature: pickup });
        }
    }
    @Input() set selectedDropoff(dropoff: GeocodeAddress) {
        if (dropoff) {
            this.planRideForm.patchValue({ dropoffFeature: dropoff });
        }
    }

    minimumAllowedDateToBookRide: Date;
    hoursBeforeDepartureWhenAllowedToPlaceOrder: number;
    departureLimitExcludeWeekends = false;
    limitSelectableTime = false;
    displayMinimumDateError = false;
    planRideForm: FormGroup;
    pickup: FormControl;
    dropoff: FormControl;
    dateAndTime = new Date();
    // max = new Date(this.dateAndTime.getDate() + this.MAX_BOOK_DATE);
    priorityOptions = BookRidePriorityType;
    dateFormat = this.env.PRIMARY_DATE_FORMAT_NGPIPE;

    assetsPath$ = this.applicationFacade.assetsPath$;
    pickupSuggestions$: Observable<GeocodeAddress[]>;
    dropoffSuggestions$: Observable<GeocodeAddress[]>;

    potentialStops$ = this.ridePlanner.potentialStops$;
    filteredStops$ = this.ridePlanner.filteredStops$;

    @Output() onSubmit = new EventEmitter<BookRidePlan>();
    @Output() onSelectedPickup = new EventEmitter<GeocodeAddress>();
    @Output() onSelectedDropoff = new EventEmitter<GeocodeAddress>();
    @Output() onPotentialStops = new EventEmitter<GeocodeAddress[]>();

    constructor(
        private ridePlanner: RidePlannerFacade,
        private applicationFacade: ApplicationFacade,
        @Inject('ENV') public env: Env,
        private appConfig: AppStartupService
    ) {}

    ngOnInit() {
        const orderDepartureLimitProperty = this.appConfig.getPropertyByCode(
            'ONDEMAND_WEB_CUSTOMER_PLACE_ORDER_MIN_HOURS_BEFORE_DEPARTURE'
        );
        if (orderDepartureLimitProperty && orderDepartureLimitProperty.value) {
            const hours = Number(orderDepartureLimitProperty.value);
            if (Number.isFinite(hours)) {
                if (this.appConfig.getPropertyByCode('ONDEMAND_WEB_ORDER_DEPARTURE_LIMIT_EXCLUDE_WEEKENDS')) {
                    this.departureLimitExcludeWeekends = true;
                }
                this.hoursBeforeDepartureWhenAllowedToPlaceOrder = hours;
                this.calculateMinimumPlacableOrderDate(hours);
                this.updateDateField(this.minimumAllowedDateToBookRide);
            }
        }
        this.pickup = new FormControl();
        this.dropoff = new FormControl();

        this.planRideForm = new FormGroup({
            dateAndTime: new FormControl(''),
            time: new FormControl(),
            priority: new FormControl('', [Validators.required]),
            pickupFeature: new FormControl('', [Validators.required]),
            dropoffFeature: new FormControl('', [Validators.required])
        });

        this.ridePlanner.returnWithPlan$.pipe(take(1)).subscribe((returnWithPlan) => {
            if (returnWithPlan.isReturn) {
                this.planRideForm.patchValue({
                    pickupFeature: returnWithPlan.plan.dropoffFeature,
                    dropoffFeature: returnWithPlan.plan.pickupFeature
                });
                this.ridePlanner.setIsReturn(false);
            }
        });

        this.pickupSuggestions$ = this.getSuggestions(this.pickup.valueChanges);
        this.dropoffSuggestions$ = this.getSuggestions(this.dropoff.valueChanges);
        if (this.env.REGIONAL_BOOKING) {
            this.ridePlanner.fetchStopsByRegion('');
        }
    }

    private getSuggestions(valueChanges: Observable<string>): Observable<GeocodeAddress[]> {
        return valueChanges.pipe(
            filter((value) => (this.env.REGIONAL_BOOKING ? value.length >= 0 : value.length >= 3)),
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((value: string) => {
                if (this.env.REGIONAL_BOOKING) {
                    this.ridePlanner.fetchFilteredStopsByRegion(value);
                    return this.filteredStops$;
                } else {
                    return this.ridePlanner.fetchGeocodeAddressEstonia(value);
                }
            })
        );
    }

    suggestionsDisplayCallback = (suggestion: GeocodeAddress) =>
        suggestion && suggestion.address + (suggestion.direction ? ' (' + suggestion.direction + ')' : '');
    optionIconDisplayCallback = (suggestion: GeocodeAddress) =>
        suggestion.type === GeocodeAddressType.BUS_STOP ? 'directions_bus_filled' : 'place';

    updateDateField(date: Date) {
        this.dateAndTime = date;
        if (this.minimumAllowedDateToBookRide) {
            this.updateTimeLimiting();
        }
    }

    updateTimeLimiting() {
        const selectedDate = new DateBuilder(this.dateAndTime).setTimeStartOfDay().getDate();
        const minDate = new DateBuilder(this.minimumAllowedDateToBookRide).setTimeStartOfDay().getDate();
        if (selectedDate.getTime() <= minDate.getTime()) {
            this.limitSelectableTime = true;
        } else {
            this.limitSelectableTime = false;
        }
    }

    setPickupValue(value: string) {
        this.pickup.setValue(value);
    }

    setDropOffValue(value: string) {
        this.dropoff.setValue(value);
    }

    onInputPickup(address: GeocodeAddress) {
        this.onSelectedPickup.emit(address);
    }

    onInputDropoff(address: GeocodeAddress) {
        this.onSelectedDropoff.emit(address);
    }

    submit() {
        if (this.planRideForm.valid) {
            const time = this.planRideForm.controls.time.value;
            this.dateAndTime.setHours(time.hour);
            this.dateAndTime.setMinutes(time.minute);
            if (this.hoursBeforeDepartureWhenAllowedToPlaceOrder) {
                this.calculateMinimumPlacableOrderDate(this.hoursBeforeDepartureWhenAllowedToPlaceOrder);
                if (this.dateAndTime < this.minimumAllowedDateToBookRide) {
                    this.displayMinimumDateError = true;
                    return;
                }
            }
            this.planRideForm.patchValue({ dateAndTime: this.dateAndTime });
            this.onSubmit.emit(this.planRideForm.value);
        } else {
            this.planRideForm.markAllAsTouched();
            this.pickup.markAsTouched();
            this.dropoff.markAsTouched();
        }
    }

    calculateMinimumPlacableOrderDate(allowedHoursBeforeDeparture: number) {
        const dateBuilder = DateBuilder.now();
        if (this.departureLimitExcludeWeekends) {
            dateBuilder.addHoursSkippingWeekends(allowedHoursBeforeDeparture);
        } else {
            dateBuilder.addHours(allowedHoursBeforeDeparture);
        }
        this.minimumAllowedDateToBookRide = dateBuilder
            .addMinutes(10) // add 10 minutes so backend calculation will still pass if user waits <10 minutes with the request
            .truncateSeconds()
            .getDate();
        this.updateTimeLimiting();
    }
}
